import React, { useState, useMemo, useEffect } from 'react'
import { Grid, Card, CardContent, Stack, Dialog, DialogActions, DialogContent, Button } from '@mui/material'
import NumberField from 'components/forms/NumberField'
import { CardTable } from 'components/datatable/NewTable'
import i18n from 'simple-react-i18n'
import { getSetting, getSettingFloat } from 'utils/SettingUtils'
import { shallowEqual, useSelector, useDispatch } from 'react-redux'
import useBoolean from 'utils/customHook/useBoolean'
import { nbPerPageLabel } from 'referencial/constants/ReferencialConstants'
import useActions from 'utils/customHook/useActions'
import { groupBy, isNil, mapValues, omitBy, pick } from 'lodash'
import AdministrationAction from 'administration/actions/AdministrationAction'
import QualityAction from 'quality/actions/QualityAction'
import ParameterAction from 'referencial/components/parameter/actions/ParameterAction'
import useTitle from 'utils/customHook/useTitle'
import { StyledFieldSet, StyledLegend } from 'components/StyledElements'
import SelectionTableModal from 'components/modal/SelectionTableModal'
import { SimpleFilterField } from 'components/datatable/SimpleSelectionTable'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import CatchmentAction from 'catchment/actions/CatchmentAction'
import { hybridSearch } from 'utils/SearchUtil'
import { normalizeString } from 'utils/StringUtil'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'
import { darkBlue, whiteColor } from 'utils/constants/ColorTheme'
import { exportFile } from 'utils/ExportDataUtil'
import ProgressCard from 'components/card/ProgressCard'
import { DEFAULT_OBJECTIVE, DEFAULT_THRESHOLD_NITRATES, DEFAULT_THRESHOLD_PESTICIDES_1, DEFAULT_THRESHOLD_PESTICIDES_2 } from 'catchment/constants/CatchmentConstant'

const OBJECTIVE_DEFAULTS = {
    objectiveNitrates: DEFAULT_OBJECTIVE,
    objectivePesticides: DEFAULT_OBJECTIVE,
    objectiveSumPesticides: DEFAULT_OBJECTIVE,
}

const SAGE_DEFAULTS = {
    sageThresholdNitrates: DEFAULT_THRESHOLD_NITRATES,
    sageThresholdPesticides: DEFAULT_THRESHOLD_PESTICIDES_1,
    sageThresholdSumPesticides: DEFAULT_THRESHOLD_PESTICIDES_2,
}

const toDefaultValues = defaults => mapValues(defaults, value => parseFloat(value))

const ObjectivesScreen = ()=>{
    const {
        applicationSettings,
        catchments,
    } = useSelector(store => ({
        applicationSettings: store.AdministrationReducer.applicationSettings,
        catchments: store.CatchmentReducer.catchments,
    }), shallowEqual)

    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(QualityAction.fetchThresholds())
        dispatch(ParameterAction.fetchParameters())
    }, [])

    const {
        value: isReadMode,
        setTrue: setReadMode,
        setFalse: setEditMode,
    } = useBoolean(true)
    const {
        value: isObjectivePopinOpen,
        setTrue: openObjectivePopinOpen,
        setFalse: closeObjectivePopinOpen,
    } = useBoolean(false)
    const {
        value: isSAGEPopinOpen,
        setTrue: openSAGEPopinOpen,
        setFalse: closeSAGEPopinOpen,
    } = useBoolean(false)
    const {
        value: isLoaded,
        setTrue: loaded,
        setFalse: loading,
    } = useBoolean(true)

    const defaultObjectiveNitrates = useApplicationSetting('NITRATES_OBJECTIVE', setting => setting ? parseFloat(setting) : undefined)
    const [objectiveNitrates, setObjectiveNitrates] = useState(() => getSettingFloat(applicationSettings, 'NITRATES_OBJECTIVE'))
    const defaultObjectiveSumPesticides = useApplicationSetting('PESTICIDES_SUM_OBJECTIVE', setting => setting ? parseFloat(setting) : undefined)
    const [objectiveSumPesticides, setObjectiveSumPesticides] = useState(() => getSettingFloat(applicationSettings, 'PESTICIDES_SUM_OBJECTIVE'))
    const defaultObjectivePesticides = useApplicationSetting('PESTICIDES_OBJECTIVE', setting => setting ? parseFloat(setting) : undefined)
    const [objectivePesticides, setObjectivePesticides] = useState(() => getSettingFloat(applicationSettings, 'PESTICIDES_OBJECTIVE'))
    const defaultSelectedCatchments = useApplicationSetting('SELECTED_CATCHMENTS', setting => setting?.split(',').filter(c => !!c).map(id => parseInt(id)) || [])
    const [selectedCatchments, setSelectedCatchments] = useState(() => getSetting(applicationSettings, 'SELECTED_CATCHMENTS')?.split(',').filter(c => !!c).map(id => parseInt(id)) || [])

    const defaultNitratesThreshold = useApplicationSetting('CATCHMENT_NITRATES_THRESHOLD', setting => setting ? parseFloat(setting) : undefined)
    const defaultPesticidesThreshold = useApplicationSetting('CATCHMENT_PESTICIDE_THRESHOLD_1', setting => setting ? parseFloat(setting) : undefined)
    const defaultSumPesticidesThreshold = useApplicationSetting('CATCHMENT_PESTICIDE_THRESHOLD_2', setting => setting ? parseFloat(setting) : undefined)

    const defaultSelectedCatchmentsSAGE = useApplicationSetting('SELECTED_CATCHMENTS_SAGE', setting => setting?.split(',').filter(c => !!c).map(id => parseInt(id)) || [])
    const [selectedCatchmentsSAGE, setSelectedCatchmentsSAGE] = useState(() => getSetting(applicationSettings, 'SELECTED_CATCHMENTS_SAGE')?.split(',').filter(c => !!c).map(id => parseInt(id)) || [])

    const [open, setOpen] = useState(false)
    const [openSAGE, setOpenSAGE] = useState(false)
    const [dataType, setDataType] = useState({})
    // Valeurs saisies dans les popins, en attente de la sauvegarde globale : { [idCaptage]: { champ: valeur } }
    const [editedCatchments, setEditedCatchments] = useState({})

    const resetState = () => {
        setObjectiveNitrates(defaultObjectiveNitrates)
        setObjectiveSumPesticides(defaultObjectiveSumPesticides)
        setObjectivePesticides(defaultObjectivePesticides)
        setSelectedCatchments(defaultSelectedCatchments)
        setSelectedCatchmentsSAGE(defaultSelectedCatchmentsSAGE)
        setEditedCatchments({})
    }

    const validatePopin = defaults => {
        const values = omitBy(mapValues(defaults, (_, field) => parseFloat(dataType[field]?.value ?? dataType[field])), Number.isNaN)
        setEditedCatchments(prev => ({ ...prev, [dataType.id]: { ...prev[dataType.id], ...values } }))
        setOpen(false)
        setOpenSAGE(false)
    }

    // Chaque tableau ne touche que ses propres champs. Un captage retiré d'un tableau repasse aux valeurs par défaut
    // de ce tableau ; les resets sont appliqués après les saisies pour l'emporter sur une saisie faite avant le retrait.
    const getCatchmentUpdates = () => {
        const edits = Object.keys(editedCatchments).map(id => {
            const catchmentId = parseInt(id)
            const fields = [
                ...(selectedCatchments.includes(catchmentId) ? Object.keys(OBJECTIVE_DEFAULTS) : []),
                ...(selectedCatchmentsSAGE.includes(catchmentId) ? Object.keys(SAGE_DEFAULTS) : []),
            ]
            return { id: catchmentId, values: pick(editedCatchments[id], fields) }
        })
        const objectiveResets = defaultSelectedCatchments
            .filter(id => !selectedCatchments.includes(id))
            .map(id => ({ id, values: toDefaultValues(OBJECTIVE_DEFAULTS) }))
        const sageResets = defaultSelectedCatchmentsSAGE
            .filter(id => !selectedCatchmentsSAGE.includes(id))
            .map(id => ({ id, values: toDefaultValues(SAGE_DEFAULTS) }))

        const updatesById = groupBy([...edits, ...objectiveResets, ...sageResets], 'id')
        return Object.keys(updatesById)
            .map(id => ({ id: parseInt(id), values: Object.assign({}, ...updatesById[id].map(u => u.values)) }))
            .filter(({ values }) => Object.keys(values).length)
    }

    const saveCatchments = () => getCatchmentUpdates().map(({ id, values }) => {
        const catchment = catchments.find(c => c.id === id)
        return catchment ? dispatch(CatchmentAction.updateCatchment(id, { ...catchment, ...values })) : Promise.resolve()
    })

    useTitle(() => {
        return [{
            title: i18n.catchments,
            href: 'catchment',
        }, {
            title: i18n.settings,
            href: 'catchment/option',
        }]
    }, [])

    useActions(() => {
        if (isReadMode) {
            return {
                edit: () => setEditMode(),
            }
        }
        return {
            cancel: () => {
                resetState()
                setReadMode()
            },
            save: () => {
                const settings = [
                    { parameter: 'NITRATES_OBJECTIVE', value: objectiveNitrates },
                    { parameter: 'PESTICIDES_SUM_OBJECTIVE', value: objectiveSumPesticides },
                    { parameter: 'PESTICIDES_OBJECTIVE', value: objectivePesticides },
                    { parameter: 'SELECTED_CATCHMENTS', value: selectedCatchments.join(',') },
                    { parameter: 'SELECTED_CATCHMENTS_SAGE', value: selectedCatchmentsSAGE.join(',') },
                ].map(p => isNil(p.value) ? p : { ...p, value: `${p.value}` }).filter(p => getSetting(applicationSettings, p.parameter) !== p.value)
                loading()
                Promise.all([
                    settings.length ? dispatch(AdministrationAction.updateSieauParameters(settings)) : Promise.resolve(),
                    ...saveCatchments(),
                ]).finally(() => {
                    setEditedCatchments({})
                    setReadMode()
                    loaded()
                })
            },
        }
    }, [applicationSettings, isReadMode, objectiveNitrates, objectiveSumPesticides, objectivePesticides, selectedCatchments, selectedCatchmentsSAGE, editedCatchments, catchments, defaultSelectedCatchments, defaultSelectedCatchmentsSAGE])

    const catchmentsFormatted = useMemo(() => selectedCatchments.map(id => {
        const catchment = catchments.find(c => c.id === id)
        const edited = editedCatchments[id] || {}
        return {
            id,
            name: { value: catchment?.name },
            objectiveNitrates: { value: edited.objectiveNitrates ?? catchment?.objectiveNitrates ?? objectiveNitrates ?? parseFloat(DEFAULT_OBJECTIVE) },
            objectiveSumPesticides: { value: edited.objectiveSumPesticides ?? catchment?.objectiveSumPesticides ?? objectiveSumPesticides ?? parseFloat(DEFAULT_OBJECTIVE) },
            objectivePesticides: { value: edited.objectivePesticides ?? catchment?.objectivePesticides ?? objectivePesticides ?? parseFloat(DEFAULT_OBJECTIVE) },
        }
    }), [selectedCatchments, catchments, editedCatchments, objectiveNitrates, objectiveSumPesticides, objectivePesticides])

    const catchmentsFormattedSAGE = useMemo(() => selectedCatchmentsSAGE.map(id => {
        const catchment = catchments.find(c => c.id === id)
        const edited = editedCatchments[id] || {}
        return {
            id,
            name: { value: catchment?.name },
            sageThresholdNitrates: { value: edited.sageThresholdNitrates ?? catchment?.sageThresholdNitrates ?? defaultNitratesThreshold ?? parseFloat(DEFAULT_THRESHOLD_NITRATES) },
            sageThresholdSumPesticides: { value: edited.sageThresholdSumPesticides ?? catchment?.sageThresholdSumPesticides ?? defaultSumPesticidesThreshold ?? parseFloat(DEFAULT_THRESHOLD_PESTICIDES_2) },
            sageThresholdPesticides: { value: edited.sageThresholdPesticides ?? catchment?.sageThresholdPesticides ?? defaultPesticidesThreshold ?? parseFloat(DEFAULT_THRESHOLD_PESTICIDES_1) },
        }
    }), [selectedCatchmentsSAGE, catchments, editedCatchments, defaultNitratesThreshold, defaultSumPesticidesThreshold, defaultPesticidesThreshold])

    const dialogTitle = dataType.name?.value ? `${i18n.customizedObjectives} - ${dataType.name.value}` : i18n.customizedObjectives

    const filterCatchments = catchments.map(c => ({ ...c, labelSearch: c.name, _normalizedSearchString: c.name }))
    return (
        <>
            {!isLoaded && <ProgressCard indeterminate /> || (
                <>
                    <Card>
                        <CardContent>
                            <Stack spacing={1}>
                                <StyledFieldSet>
                                    <StyledLegend>{i18n.objectivesThreshold}</StyledLegend>
                                    <Grid container sx={{ paddingTop: '0.5rem' }} rowSpacing={0.5} columnSpacing={1} alignItems='flex-start'>
                                        <Grid size={3}>
                                            <NumberField
                                                floatValue
                                                title={`${i18n.objectiveNitrates} (%)`}
                                                value={objectiveNitrates}
                                                onChange={setObjectiveNitrates}
                                                disabled={isReadMode}
                                            />
                                        </Grid>
                                        <Grid size={3}>
                                            <NumberField
                                                floatValue
                                                title={`${i18n.objectivePesticides} (%)`}
                                                value={objectivePesticides}
                                                onChange={setObjectivePesticides}
                                                disabled={isReadMode}
                                            />
                                        </Grid>
                                        <Grid size={3}>
                                            <NumberField
                                                floatValue
                                                title={`${i18n.objectiveSumPesticides} (%)`}
                                                value={objectiveSumPesticides}
                                                onChange={setObjectiveSumPesticides}
                                                disabled={isReadMode}
                                            />
                                        </Grid>
                                    </Grid>
                                </StyledFieldSet>
                                <CardTable
                                    title={i18n.objectivesByCatchment}
                                    actions={[{
                                        icon: 'edit',
                                        tooltip: i18n.edit,
                                        onClick: openObjectivePopinOpen,
                                        displayed: !isReadMode,
                                        color: whiteColor,
                                    },
                                    {
                                        icon: 'file_download',
                                        tooltip: i18n.export,
                                        onClick: () => {
                                            const exportDataFormated = catchmentsFormatted.map(p => ({
                                                id: p.id,
                                                name: p.name.value,
                                                objectiveNitrates: p.objectiveNitrates.value,
                                                objectivePesticides: p.objectivePesticides.value,
                                                objectiveSumPesticides: p.objectiveSumPesticides.value,
                                                headers: ['id', 'name', 'objectiveNitrates', 'objectivePesticides', 'objectiveSumPesticides'],
                                            }))
                                            exportFile({
                                                data: exportDataFormated,
                                                exportType: 'xlsx',
                                                titleFile: i18n.objectivesByCatchment.split(' ').join('_'),
                                            })
                                        },
                                        color: whiteColor,
                                    }]}
                                    lineActions={[
                                        {
                                            icon: 'edit',
                                            onClick: (s) => {
                                                setOpen(true)
                                                setDataType(s)
                                            },
                                            displayed: !isReadMode,
                                        },
                                        {
                                            icon: 'delete',
                                            onClick: (unselectedCatchment) => {
                                                setSelectedCatchments(prev =>
                                                    prev.filter(p => p !== unselectedCatchment.id),
                                                )
                                            },
                                            displayed: !isReadMode,
                                        },
                                    ]}
                                    color={darkBlue}
                                    rows={catchmentsFormatted}
                                    headers={[
                                        { key: 'id', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.id}</span>) },
                                        { key: 'name', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.name}</span>) },
                                        { key: 'objectiveNitrates', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{`${i18n.objectiveNitrates} (%)`}</span>) },
                                        { key: 'objectivePesticides', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{`${i18n.objectivePesticides} (%)`}</span>) },
                                        { key: 'objectiveSumPesticides', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{`${i18n.objectiveSumPesticides} (%)`}</span>) },
                                    ]}
                                    rowsPerPageOptions={nbPerPageLabel}
                                />
                                <CardTable
                                    title={i18n.sageThresholdByCatchment}
                                    actions={[{
                                        icon: 'edit',
                                        tooltip: i18n.edit,
                                        onClick: openSAGEPopinOpen,
                                        displayed: !isReadMode,
                                        color: whiteColor,
                                    },
                                    {
                                        icon: 'file_download',
                                        tooltip: i18n.export,
                                        onClick: () => {
                                            const exportDataFormated = catchmentsFormattedSAGE.map(p => ({
                                                id: p.id,
                                                name: p.name.value,
                                                sageThresholdNitrates: p.sageThresholdNitrates.value,
                                                sageThresholdPesticides: p.sageThresholdPesticides.value,
                                                sageThresholdSumPesticides: p.sageThresholdSumPesticides.value,
                                                headers: ['id', 'name', 'sageThresholdNitrates', 'sageThresholdPesticides', 'sageThresholdSumPesticides'],

                                            }))
                                            exportFile({
                                                data: exportDataFormated,
                                                exportType: 'xlsx',
                                                titleFile: i18n.sageThresholdByCatchment.split(' ').join('_'),
                                            })
                                        },
                                        color: whiteColor,
                                    }]}
                                    lineActions={[
                                        {
                                            icon: 'edit',
                                            onClick: (s) => {
                                                setOpenSAGE(true)
                                                setDataType(s)
                                            },
                                            displayed: !isReadMode,
                                        },
                                        {
                                            icon: 'delete',
                                            onClick: (unselectedCatchment) => {
                                                setSelectedCatchmentsSAGE(prev =>
                                                    prev.filter(p => p !== unselectedCatchment.id),
                                                )
                                            },
                                            displayed: !isReadMode,
                                        },
                                    ]}
                                    color={darkBlue}
                                    rows={catchmentsFormattedSAGE}
                                    headers={[
                                        { key: 'id', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.id}</span>) },
                                        { key: 'name', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.name}</span>) },
                                        { key: 'sageThresholdNitrates', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{`${i18n.sageThresholdNitrates} (mg/L)`}</span>) },
                                        { key: 'sageThresholdPesticides', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{`${i18n.sageThresholdPesticides} (µg/L)`}</span>) },
                                        { key: 'sageThresholdSumPesticides', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{`${i18n.sageThresholdSumPesticides} (µg/L)`}</span>) },
                                    ]}
                                    rowsPerPageOptions={nbPerPageLabel}
                                />
                            </Stack>
                        </CardContent>
                    </Card>
                    <SelectionTableModal
                        isOpen={isObjectivePopinOpen}
                        onClose={closeObjectivePopinOpen}
                        onValidate={list => {
                            closeObjectivePopinOpen()
                            setSelectedCatchments(list)
                        }}
                        title={i18n.customizedCatchmentsObjectives}

                        listData={filterCatchments}
                        defaultSelectionList={selectedCatchments}

                        listHeaders={['name']}
                        listTitle={i18n.defaultThresholds}
                        selectionListTitle={i18n.customizedThresholds}
                        maxHeightTable={'53vh'}

                        filterField={SimpleFilterField}
                        filterFunction={(list, { searchValue }) => hybridSearch(
                            list,
                            normalizeString(searchValue),
                        )}
                    />
                    <SelectionTableModal
                        isOpen={isSAGEPopinOpen}
                        onClose={closeSAGEPopinOpen}
                        onValidate={list => {
                            closeSAGEPopinOpen()
                            setSelectedCatchmentsSAGE(list)
                        }}
                        title={i18n.customizedCatchmentsObjectives}

                        listData={filterCatchments}
                        defaultSelectionList={selectedCatchmentsSAGE}

                        listHeaders={['name']}
                        listTitle={i18n.defaultThresholds}
                        selectionListTitle={i18n.customizedThresholds}
                        maxHeightTable={'53vh'}

                        filterField={SimpleFilterField}
                        filterFunction={(list, { searchValue }) => hybridSearch(
                            list,
                            normalizeString(searchValue),
                        )}
                    />
                    <Dialog
                        maxWidth='lg'
                        open={open}
                    >
                        <DefaultDialogTitle title={dialogTitle} onClose={() => setOpen(false)} />
                        <DialogContent>
                            <Grid container rowSpacing={0.5} columnSpacing={1}>
                                <Grid size={3}>
                                    <NumberField
                                        title={`${i18n.objectiveNitrates} (%)`}
                                        value={dataType.objectiveNitrates ? dataType.objectiveNitrates?.value ?? dataType.objectiveNitrates : undefined}
                                        onChange={v => setDataType({ ...dataType, objectiveNitrates: { value: v } })}
                                        disabled={isReadMode}
                                        floatValue
                                    />
                                </Grid>
                                <Grid size={3}>
                                    <NumberField
                                        title={`${i18n.objectivePesticides} (%)`}
                                        value={dataType.objectivePesticides ? dataType.objectivePesticides?.value ?? dataType.objectivePesticides : undefined}
                                        onChange={v => setDataType({ ...dataType, objectivePesticides: { value: v } })}
                                        disabled={isReadMode}
                                        floatValue
                                    />
                                </Grid>
                                <Grid size={3}>
                                    <NumberField
                                        title={`${i18n.objectiveSumPesticides} (%)`}
                                        value={dataType.objectiveSumPesticides ? dataType.objectiveSumPesticides?.value ?? dataType.objectiveSumPesticides : undefined}
                                        onChange={v => setDataType({ ...dataType, objectiveSumPesticides: { value: v } })}
                                        disabled={isReadMode}
                                        floatValue
                                    />
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => validatePopin(OBJECTIVE_DEFAULTS)} variant='contained'>
                                {i18n.validate}
                            </Button>
                        </DialogActions>
                    </Dialog>
                    <Dialog
                        maxWidth='lg'
                        open={openSAGE}
                    >
                        <DefaultDialogTitle title={dialogTitle} onClose={() => setOpenSAGE(false)} />
                        <DialogContent>
                            <Grid container rowSpacing={0.5} columnSpacing={1}>
                                <Grid size={3}>
                                    <NumberField
                                        title={`${i18n.sageThresholdNitrates} (mg/L)`}
                                        value={dataType.sageThresholdNitrates ? dataType.sageThresholdNitrates?.value ?? dataType.sageThresholdNitrates : undefined}
                                        onChange={v => setDataType({ ...dataType, sageThresholdNitrates: { value: v } })}
                                        disabled={isReadMode}
                                        floatValue
                                    />
                                </Grid>
                                <Grid size={3}>
                                    <NumberField
                                        title={`${i18n.sageThresholdPesticides} (µg/L)`}
                                        value={dataType.sageThresholdPesticides ? dataType.sageThresholdPesticides?.value ?? dataType.sageThresholdPesticides : undefined}
                                        onChange={v => setDataType({ ...dataType, sageThresholdPesticides: { value: v } })}
                                        disabled={isReadMode}
                                        floatValue
                                    />
                                </Grid>
                                <Grid size={3}>
                                    <NumberField
                                        title={`${i18n.sageThresholdSumPesticides} (µg/L)`}
                                        value={dataType.sageThresholdSumPesticides ? dataType.sageThresholdSumPesticides?.value ?? dataType.sageThresholdSumPesticides : undefined}
                                        onChange={v => setDataType({ ...dataType, sageThresholdSumPesticides: { value: v } })}
                                        disabled={isReadMode}
                                        floatValue
                                    />
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => validatePopin(SAGE_DEFAULTS)} variant='contained'>
                                {i18n.validate}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </>
            )}
        </>
    )
}
export default ObjectivesScreen